import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
  CartesianGrid, LineChart, Line,
} from "recharts";
import { ShieldAlert, ArrowRight } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { StatCard } from "@/components/common/StatCard";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { mxn } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { esActividadVulnerable } from "@/lib/actividad_vulnerable";

const PALETTE = ["#1B2E1F", "#2E5A3A", "#10B981", "#F59E0B", "#DC2626", "#6E8C5A"];
const AXIS_TICK = { fontSize: 11, fill: "#64748b" };

function ChartCard({ title, children, testid }) {
  return (
    <div data-testid={testid} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <h3 className="mb-4 font-heading text-sm font-semibold text-slate-700">{title}</h3>
      <div style={{ width: "100%", height: 256 }}>{children}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user, giro } = useAuth();
  // ago-2026 · Bloque A — una entidad de Actividad Vulnerable no tiene tablero SOFOM: su
  // inicio es el del vertical. Antes aterrizaba aquí y veía "Módulo de otro régimen".
  const esAV = esActividadVulnerable(giro);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [integ, setInteg] = useState(null);
  const [roles, setRoles] = useState(null);
  const [ebrGate, setEbrGate] = useState(null); // {aprobada, ya_opera, debe_bloquear, debe_avisar}
  const [ocStatus, setOcStatus] = useState(null); // {visible, motivo, oc_user_id, oc_email}
  const [ocResendBusy, setOcResendBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // ago-2026 — los `.catch(() => {})` silenciosos dejaban el tablero girando para siempre
    // ante cualquier fallo (un 401 de sesión, por ejemplo). Ahora el error de la carga
    // principal se muestra en pantalla y los secundarios quedan en consola.
    setError(null);
    const aviso = (ruta) => (e) => console.warn(`Dashboard: ${ruta} falló →`, apiError(e));
    api.get("/dashboard").then((r) => setData(r.data)).catch((e) => setError(apiError(e)));
    if (user && user.role !== "super_admin") {
      api.get("/dashboard/integraciones").then((r) => setInteg(r.data)).catch(aviso("/dashboard/integraciones"));
      api.get("/dashboard/roles").then((r) => setRoles(r.data)).catch(aviso("/dashboard/roles"));
      api.get("/riesgo/metodologia/gate-estado").then((r) => setEbrGate(r.data)).catch(aviso("/riesgo/metodologia/gate-estado"));
      api.get("/dashboard/oc-status").then((r) => setOcStatus(r.data)).catch(aviso("/dashboard/oc-status"));
    }
  }, [user]);

  // Bloque A — el redirect del tablero SOFOM vive en `AppLayout` (el candado por vertical
  // corta antes de que este componente se monte). Aquí `esAV` solo decide los textos.

  if (error && !data)
    return (
      <div data-testid="dashboard-error"
           className="flex h-full flex-col items-center justify-center gap-3 p-10 text-center">
        <ShieldAlert className="h-10 w-10 text-red-400" strokeWidth={1.5} />
        <h2 className="font-heading text-lg font-semibold text-slate-700">
          No pudimos cargar el tablero
        </h2>
        <p data-testid="dashboard-error-msg" className="max-w-md text-sm text-slate-500">{error}</p>
        <Button data-testid="dashboard-error-retry" onClick={() => window.location.reload()}>
          Volver a intentar
        </Button>
      </div>
    );

  if (!data)
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-primary" />
      </div>
    );

  if (data.saas || user?.role === "super_admin") {
    return <SaasDashboard data={data} user={user} />;
  }

  const k = data.kpis;
  const c = data.charts;

  const cards = [
    { label: "Entidades activas", value: k.entidades_activas, icon: "Building2", accent: "primary" },
    { label: "Clientes registrados", value: k.clientes_registrados, icon: "Users", accent: "blue" },
    { label: "Expedientes completos", value: k.expedientes_completos, icon: "FolderCheck", accent: "green" },
    { label: "Expedientes incompletos", value: k.expedientes_incompletos, icon: "FolderMinus", accent: "amber" },
    { label: "Alertas PLD abiertas", value: k.alertas_abiertas, icon: "ShieldAlert", accent: "red" },
    { label: "Alertas PLD cerradas", value: k.alertas_cerradas, icon: "ShieldCheck", accent: "green" },
    { label: "Créditos activos", value: k.creditos_activos, icon: "Wallet", accent: "primary" },
    { label: "Cartera total", value: mxn(k.cartera_total), icon: "Landmark", accent: "blue" },
    { label: "Cartera vigente", value: mxn(k.cartera_vigente), icon: "TrendingUp", accent: "green" },
    { label: "Cartera vencida", value: mxn(k.cartera_vencida), icon: "TrendingDown", accent: "red" },
    { label: "Pagos del mes", value: mxn(k.pagos_mes), icon: "Banknote", accent: "green" },
    { label: "Reportes pendientes", value: k.reportes_pendientes, icon: "FileClock", accent: "amber" },
    { label: "Avisos por vencer", value: k.avisos_por_vencer, icon: "CalendarClock", accent: "amber" },
    { label: "Reclamaciones CONDUSEF", value: k.reclamaciones_condusef, icon: "Scale", accent: "blue" },
    { label: "Quejas REDECO", value: k.quejas_redeco, icon: "MessageSquareWarning", accent: "amber" },
    { label: "Operaciones inusuales", value: k.operaciones_inusuales, icon: "AlertTriangle", accent: "red" },
    { label: "Riesgo promedio clientes", value: `${k.riesgo_promedio} / 3`, icon: "Gauge", accent: "primary" },
    { label: "Cumplimiento documental", value: `${k.cumplimiento_documental}%`, icon: "ClipboardCheck", accent: "green" },
    // Fase 5 (feb-2026) — Tarjetas de líneas revolventes. Condicionales: solo aparecen si
    // la entidad tiene al menos UNA línea activa. Una SOFOM que solo hace crédito simple
    // no verá estos números (evita ruido de dos ceros permanentes). Cuando alguien crea la
    // primera línea, ambas tarjetas se materializan automáticamente en el siguiente refresh.
    ...(Number(k.lineas_activas) > 0 ? [
      { label: "Líneas · autorizado", value: mxn(k.lineas_autorizado), icon: "Wallet", accent: "blue" },
      { label: "Líneas · disponible", value: mxn(k.lineas_disponible), icon: "CircleDollarSign", accent: "green" },
    ] : []),
  ];

  return (
    <div>
      <PageHeader
        title="Dashboard general"
        subtitle={`Bienvenido, ${user?.name?.split(" ")[0] || ""}. Panorama de cumplimiento y operación.`}
      />
      <div className="space-y-6 p-6 md:p-8">
        {ocStatus?.visible && (
          <div data-testid="dashboard-banner-oc"
            className="flex flex-wrap items-start gap-3 rounded-lg border-2 border-red-300 bg-red-50 px-4 py-3.5 shadow-sm">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-100">
              <ShieldAlert className="h-4.5 w-4.5 text-red-700" />
            </span>
            <div className="min-w-[240px] flex-1 text-sm">
              {ocStatus.motivo === "sin_oc" ? (
                <>
                  <p data-testid="dashboard-banner-oc-title-sin-oc" className="font-heading font-semibold text-red-900">
                    Tu entidad no tiene Oficial de Cumplimiento designado.
                  </p>
                  <p className="mt-0.5 text-xs text-red-800">
                    Por Art. 20 CUB (Disposiciones de carácter general aplicables a SOFOM ENR),
                    debes designar y crear al Oficial de Cumplimiento como usuario de tu entidad
                    para poder editar catálogos SITI y emitir dictámenes 39ª.
                  </p>
                  <p className="mt-0.5 text-[11px] text-red-700">
                    Ve a Usuarios y roles → agrega un usuario con rol &quot;Oficial de Cumplimiento&quot;.
                  </p>
                </>
              ) : (
                <>
                  <p data-testid="dashboard-banner-oc-title-invitacion" className="font-heading font-semibold text-red-900">
                    Tu Oficial de Cumplimiento no recibió el correo de invitación.
                  </p>
                  <p className="mt-0.5 text-xs text-red-800">
                    El usuario del Oficial se creó correctamente
                    {ocStatus.oc_email ? <> (<span className="font-mono">{ocStatus.oc_email}</span>)</> : null},
                    pero la invitación para definir su contraseña no pudo enviarse durante el alta.
                    Reenvíala para que pueda activar su cuenta y comenzar a operar.
                  </p>
                </>
              )}
            </div>
            {ocStatus.motivo === "sin_oc" ? (
              <Button data-testid="dashboard-banner-oc-cta-crear" size="sm"
                onClick={() => navigate("/usuarios-roles")}
                className="bg-red-600 text-white hover:bg-red-700">
                Ir a Usuarios y roles <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            ) : (
              <Button data-testid="dashboard-banner-oc-cta-reenviar" size="sm"
                disabled={ocResendBusy || !ocStatus.oc_user_id}
                onClick={async () => {
                  if (!ocStatus.oc_user_id) return;
                  setOcResendBusy(true);
                  try {
                    await api.post(`/usuarios/${ocStatus.oc_user_id}/reenviar-invitacion`);
                    const r = await api.get("/dashboard/oc-status");
                    setOcStatus(r.data);
                  } catch (_e) { /* toast lo maneja api interceptor si aplica */ }
                  finally { setOcResendBusy(false); }
                }}
                className="bg-red-600 text-white hover:bg-red-700">
                {ocResendBusy ? "Reenviando…" : "Reenviar invitación"} <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            )}
          </div>
        )}
        {ebrGate?.debe_avisar && (
          <div data-testid="dashboard-banner-ebr"
            className="flex flex-wrap items-start gap-3 rounded-lg border-2 border-amber-300 bg-amber-50 px-4 py-3.5 shadow-sm">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100">
              <ShieldAlert className="h-4.5 w-4.5 text-amber-700" />
            </span>
            <div className="min-w-[240px] flex-1 text-sm">
              {/* feb-2026 · iter 07-26: ramificación por giro del banner EBR.
                  - SOFOM: texto y CTA ORIGINALES INTACTOS (régimen CNBV, "Oficial de
                    Cumplimiento", ruta /pld?tab=riesgo-ebr).
                  - Inmobiliaria (Fr. V LFPIORPI): responsable de cumplimiento neutro,
                    fundamento Art. 18 Fr. VII (evaluación basada en riesgos), CTA al EBR
                    del vertical dentro del módulo Cumplimiento. */}
              {esAV ? (
                <>
                  <p className="font-heading font-semibold text-amber-900">
                    Tu Metodología EBR aún no ha sido aprobada por el responsable de cumplimiento.
                  </p>
                  <p className="mt-0.5 text-xs text-amber-800">
                    {ebrGate.debe_bloquear
                      ? "Mientras no la apruebes, tu entidad no puede dar de alta prospectos ni clientes. Apruébala en Cumplimiento → EBR."
                      : "Tu entidad ya opera, así que las altas actuales no se bloquean. Aun así, es obligatorio que el responsable de cumplimiento apruebe la metodología. Apruébala en Cumplimiento → EBR."}
                  </p>
                  <p className="mt-0.5 text-[11px] text-amber-700">
                    Fundamento: LFPIORPI Art. 18 fracción VII.
                  </p>
                </>
              ) : (
                <>
                  <p className="font-heading font-semibold text-amber-900">
                    Tu Metodología EBR aún no ha sido aprobada por tu Oficial de Cumplimiento.
                  </p>
                  <p className="mt-0.5 text-xs text-amber-800">
                    {ebrGate.debe_bloquear
                      ? "Mientras no la apruebes, tu entidad NO puede dar de alta prospectos ni clientes. Apruébala en PLD/CNBV → Riesgo/EBR."
                      : "Tu entidad ya opera, así que tus altas actuales no se bloquean. Aun así, es obligatorio que tu Oficial de Cumplimiento apruebe la metodología. Apruébala en PLD/CNBV → Riesgo/EBR."}
                  </p>
                </>
              )}
            </div>
            <Button data-testid="dashboard-banner-ebr-cta" size="sm"
              onClick={() => navigate(esAV ? "/inmobiliaria/cumplimiento?tab=ebr" : "/pld?tab=riesgo-ebr")}
              className="bg-amber-600 text-white hover:bg-amber-700">
              {esAV ? "Ir a Cumplimiento / EBR" : "Ir a Riesgo/EBR"} <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        )}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
          {cards.map((card, i) => (
            <StatCard key={card.label} testid={`kpi-${i}`} delay={i * 30} {...card} />
          ))}
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <ChartCard title="Cartera por estatus (saldo insoluto)" testid="chart-cartera">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={c.cartera_por_estatus}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="name" tick={AXIS_TICK} />
                <YAxis tick={AXIS_TICK} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => mxn(v)} />
                <Bar dataKey="value" fill="#1B2E1F" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Alertas por nivel de riesgo" testid="chart-alertas">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={c.alertas_por_riesgo} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                  {c.alertas_por_riesgo.map((e) => (
                    <Cell key={e.name} fill={e.name === "Alto" ? "#DC2626" : e.name === "Medio" ? "#F59E0B" : "#10B981"} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Créditos colocados por mes" testid="chart-creditos">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={c.creditos_por_mes}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="name" tick={AXIS_TICK} />
                <YAxis tick={AXIS_TICK} allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="value" stroke="#2E5A3A" strokeWidth={2.5} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Clientes por nivel de riesgo" testid="chart-clientes-riesgo">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={c.clientes_por_riesgo} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                <XAxis type="number" tick={AXIS_TICK} allowDecimals={false} />
                <YAxis type="category" dataKey="name" tick={AXIS_TICK} width={60} />
                <Tooltip />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {c.clientes_por_riesgo.map((e) => (
                    <Cell key={e.name} fill={e.name === "Alto" ? "#DC2626" : e.name === "Medio" ? "#F59E0B" : "#10B981"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Reportes regulatorios por estado" testid="chart-reportes">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={c.reportes_por_estado} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={90} label>
                  {c.reportes_por_estado.map((e, i) => <Cell key={e.name} fill={PALETTE[i % PALETTE.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>

          <div className="flex flex-col justify-center rounded-lg border border-slate-200 bg-primary p-6 text-white shadow-sm">
            <p className="font-heading text-lg font-semibold">SOFOM ENR — Vertical activo</p>
            <p className="mt-2 text-sm text-slate-200">
              Operación crediticia, cartera, cobranza, PLD/CNBV, CONDUSEF y calendario regulatorio
              integrados. Plataforma multi-régimen lista para activar nuevos verticales.
            </p>
            <p className="mt-4 text-xs text-emerald-300/80">
              Herramienta de gestión regulatoria. Sujeta a parametrización conforme a políticas internas.
            </p>
          </div>
        </div>

        {integ && (
          <div>
            <h2 className="mb-3 font-heading text-base font-semibold text-slate-800">Centro de Integraciones</h2>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
              <StatCard label="Integraciones activas" value={integ.integraciones_activas} icon="Plug" accent="primary" />
              <StatCard label="Consultas SIC del mes" value={integ.consultas_mes} icon="FileSearch" accent="blue" />
              <StatCard label="Consultas exitosas" value={integ.consultas_exitosas} icon="CheckCircle2" accent="green" />
              <StatCard label="Consultas fallidas" value={integ.consultas_fallidas} icon="XCircle" accent="red" />
              <StatCard label="Clientes no hit" value={integ.clientes_no_hit} icon="UserX" accent="amber" />
              <StatCard label="Score promedio" value={integ.score_promedio} icon="Gauge" accent="primary" />
              <StatCard label="Quejas REDECO" value={integ.quejas_redeco_abiertas} icon="MessageSquareWarning" accent="amber" />
              <StatCard label="Reclamaciones REUNE" value={integ.reclamaciones_reune_abiertas} icon="Scale" accent="blue" />
              <StatCard label="Acuses pendientes" value={integ.acuses_pendientes} icon="FileClock" accent="amber" />
              <StatCard label="Cartera pend. SIC" value={integ.reportes_cartera_pendientes} icon="FileBarChart" accent="amber" />
              <StatCard label="Errores críticos" value={integ.errores_criticos} icon="AlertTriangle" accent="red" />
            </div>
          </div>
        )}

        {roles && (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div data-testid="dash-oficial" className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="mb-4 flex items-center gap-2 font-heading text-sm font-semibold text-slate-700"><ShieldAlertIcon /> Dashboard Oficial de Cumplimiento</h3>
              <div className="grid grid-cols-2 gap-3">
                {[["Alertas por inconsistencias", roles.oficial.alertas_inconsistencia], ["Clientes riesgo alto (reporte)", roles.oficial.clientes_riesgo_alto_credito], ["Casos PLD por crédito", roles.oficial.casos_pld_credito], ["Quejas/reclamaciones", roles.oficial.quejas_reclamaciones], ["Expedientes discrepantes", roles.oficial.expedientes_discrepantes], ["Alertas abiertas", roles.oficial.alertas_abiertas]].map(([l, v]) => (
                  <div key={l} className="rounded-md border border-slate-100 bg-slate-50 p-3">
                    <p className="text-[11px] uppercase tracking-wide text-slate-500">{l}</p>
                    <p className="mt-1 font-heading text-xl font-semibold text-slate-900">{v}</p>
                  </div>
                ))}
              </div>
            </div>
            <div data-testid="dash-director" className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="mb-4 flex items-center gap-2 font-heading text-sm font-semibold text-slate-700"><BriefcaseIcon /> Dashboard Director General</h3>
              <div className="grid grid-cols-2 gap-3">
                {[["Integraciones OK", roles.director.integraciones_ok], ["Cartera reportada a SIC", roles.director.cartera_reportada_sic], ["Índice aprobación", `${roles.director.indice_aprobacion}%`], ["Índice rechazo", `${roles.director.indice_rechazo}%`], ["Score prom. solicitantes", roles.director.score_promedio_solicitantes], ["Quejas abiertas", roles.director.quejas_abiertas]].map(([l, v]) => (
                  <div key={l} className="rounded-md border border-slate-100 bg-slate-50 p-3">
                    <p className="text-[11px] uppercase tracking-wide text-slate-500">{l}</p>
                    <p className="mt-1 font-heading text-xl font-semibold text-slate-900">{v}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ShieldAlertIcon() { return <span className="text-primary"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg></span>; }
function BriefcaseIcon() { return <span className="text-primary"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"><rect width="20" height="14" x="2" y="7" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg></span>; }

function SaasDashboard({ data, user }) {
  const k = data.kpis || {};
  const c = data.charts || {};
  const cards = [
    { label: "Entidades totales", value: k.total_entidades, icon: "Building2", accent: "primary" },
    { label: "Entidades activas", value: k.entidades_activas, icon: "ShieldCheck", accent: "green" },
    { label: "Entidades suspendidas", value: k.entidades_suspendidas, icon: "XCircle", accent: "red" },
    { label: "MRR (ingreso recurrente)", value: mxn(k.mrr), icon: "Banknote", accent: "green" },
    { label: "Al corriente", value: k.al_corriente, icon: "CheckCircle2", accent: "green" },
    { label: "Pago vencido", value: k.vencidas, icon: "AlertTriangle", accent: "amber" },
    { label: "Usuarios en plataforma", value: k.total_usuarios, icon: "Users", accent: "blue" },
    { label: "Planes contratados", value: k.planes_distintos, icon: "Gauge", accent: "primary" },
  ];
  return (
    <div data-testid="saas-dashboard">
      <PageHeader title="Panel de negocio SaaS"
        subtitle={`Bienvenido, ${user?.name?.split(" ")[0] || ""}. Salud del negocio FELVORA — sin datos operativos de las entidades.`} />
      <div className="space-y-6 p-6 md:p-8">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {cards.map((card, i) => <StatCard key={card.label} testid={`saas-kpi-${i}`} delay={i * 30} {...card} />)}
        </div>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <ChartCard title="Entidades por plan" testid="saas-chart-plan">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={c.por_plan}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="name" tick={AXIS_TICK} />
                <YAxis tick={AXIS_TICK} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="value" fill="#1B2E1F" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard title="Entidades por régimen" testid="saas-chart-regimen">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={c.por_regimen} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                  {(c.por_regimen || []).map((e, i) => <Cell key={e.name} fill={PALETTE[i % PALETTE.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard title="Estatus de entidades" testid="saas-chart-estatus">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={c.por_estatus} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={90} label>
                  {(c.por_estatus || []).map((e) => <Cell key={e.name} fill={e.name === "Activas" ? "#10B981" : "#DC2626"} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
        <div className="rounded-lg border border-slate-200 bg-primary p-6 text-white shadow-sm">
          <p className="font-heading text-lg font-semibold">FELVORA — Plataforma pura</p>
          <p className="mt-2 text-sm text-slate-200">
            Esta cuenta administra el negocio SaaS (entidades, suscripciones, pagos y uso del plan).
            No tiene acceso a cartera, expedientes, clientes ni alertas de las SOFOMes.
          </p>
        </div>
      </div>
    </div>
  );
}

